import { BaseApi, HttpMethodEnum } from './BaseApi/BaseApi';
import { 
    IBillingBatchFilter, 
    IBillingBatchList,
    IBillingBatch,
    ICreateBillingBatchRequest,
    BillingBatchList,
    BillingBatch,
    BillingBatchFilter,
    CreateBillingBatchRequest
} from '../entities/TestKitOrder/BillingTypes';

/**
 * Step 41: BillingBatchAPI - API service for billing batch management
 * Provides methods to interact with billing batch endpoints for the new billing batch workflow
 */
export class BillingBatchAPI extends BaseApi {
    private static baseApiUrl: string = `${BaseApi.apiUrl}/BillingBatch`;

    public static apiUrls = {
        getBillingBatches: `${BillingBatchAPI.baseApiUrl}/List`,
        getBillingBatch: `${BillingBatchAPI.baseApiUrl}/:id`,
        createBillingBatch: `${BillingBatchAPI.baseApiUrl}/Create`,
        createAndExportBatch: `${BillingBatchAPI.baseApiUrl}/CreateAndExportLineItems`, // Updated to use line items
        exportBatch: `${BillingBatchAPI.baseApiUrl}/:id/ExportLineItems`, // Updated to use line items
        deleteBillingBatch: `${BillingBatchAPI.baseApiUrl}/:id`,
    };

    /**
     * Get billing batches with filtering and pagination
     * Maps to POST /BillingBatch/List endpoint
     */
    public static async getBillingBatches(filter: IBillingBatchFilter): Promise<BillingBatchList> {
        return this.sendApiRequest<IBillingBatchList>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.getBillingBatches,
            data: new BillingBatchFilter(filter).toApiObject() as any,
        }).then(response => new BillingBatchList(response));
    }

    /**
     * Get a specific billing batch by ID with complete details including orders
     * Maps to GET /BillingBatch/{id} endpoint
     */
    public static async getBillingBatch(id: number): Promise<BillingBatch | null> {
        try {
            const response = await this.sendApiRequest<IBillingBatch>({
                method: HttpMethodEnum.GET,
                path: this.apiUrls.getBillingBatch,
                id: id.toString(),
            });
            return new BillingBatch(response);
        } catch (error: any) {
            // Return null for 404 Not Found
            if (error?.status === 404) 
                return null;
            
            throw error;
        }
    }

    /**
     * Create a new billing batch with the specified orders
     * Maps to POST /BillingBatch/Create endpoint
     */
    public static async createBillingBatch(request: ICreateBillingBatchRequest): Promise<{
        success: boolean;
        batchID: number;
        batch: BillingBatch;
        message: string;
    }> {
        const response = await this.sendApiRequest<{
            success: boolean;
            batchID: number;
            batch: IBillingBatch;
            message: string;
        }>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.createBillingBatch,
            data: new CreateBillingBatchRequest(request).toApiObject() as any,
        });

        return {
            ...response,
            batch: new BillingBatch(response.batch),
        };
    }

    /**
     * Create a billing batch and immediately export it as a CSV file with line items
     * This is the combined "Bill & Export" functionality with detailed line item format
     * Maps to POST /BillingBatch/CreateAndExportLineItems endpoint
     */
    public static async createBillingBatchAndExportCSV(request: ICreateBillingBatchRequest): Promise<Blob> {
        return this.sendApiRequest<Blob>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.createAndExportBatch,
            data: new CreateBillingBatchRequest(request).toApiObject() as any,
            extraOptionsConfig: { responseType: 'blob' }, // This ensures we get the CSV file as a blob
        });
    }

    /**
     * Re-export an existing billing batch as a CSV file with line items
     * Allows users to re-download previously created batches in detailed format
     * Maps to GET /BillingBatch/{id}/ExportLineItems endpoint
     */
    public static async exportBillingBatchCSV(id: number): Promise<Blob> {
        return this.sendApiRequest<Blob>({
            method: HttpMethodEnum.GET,
            path: this.apiUrls.exportBatch,
            id: id.toString(),
            extraOptionsConfig: { responseType: 'blob' }, // This ensures we get the CSV file as a blob
        });
    }

    /**
     * Delete a billing batch and remove batch references from orders
     * Maps to DELETE /BillingBatch/{id} endpoint
     */
    public static async deleteBillingBatch(id: number): Promise<{
        success: boolean;
        message: string;
    }> {
        return this.sendApiRequest<{
            success: boolean;
            message: string;
        }>({
            method: HttpMethodEnum.DELETE,
            path: this.apiUrls.deleteBillingBatch,
            id: id.toString(),
        });
    }

    /**
     * Helper method to download a CSV blob as a file
     * Can be used with createBillingBatchAndExportCSV() and exportBillingBatchCSV()
     */
    public static downloadCSVFile(csvBlob: Blob, filename?: string): void {
        const url = window.URL.createObjectURL(csvBlob);
        const link = document.createElement('a');
        link.href = url;
        
        // Use provided filename or generate a default one with line items indication
        link.download = filename || `AppMed_TestKit_LineItems_${new Date().toISOString().split('T')[0]}.csv`;
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    /**
     * Helper method to get all batches with default filters
     * Convenience method for the most common use case
     */
    public static async getAllBatches(pageNumber: number = 1, pageSize: number = 25): Promise<BillingBatchList> {
        const filter: IBillingBatchFilter = {
            pageNumber,
            pageSize,
            sortBy: 'CreatedDateTime',
            sortDirection: 'DESC',
        };

        return this.getBillingBatches(filter);
    }

    /**
     * Helper method to get recent batches (last 30 days)
     * Convenience method with pre-configured filter
     */
    public static async getRecentBatches(pageNumber: number = 1, pageSize: number = 25): Promise<BillingBatchList> {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        const filter: IBillingBatchFilter = {
            startDate: thirtyDaysAgo,
            pageNumber,
            pageSize,
            sortBy: 'CreatedDateTime',
            sortDirection: 'DESC',
        };

        return this.getBillingBatches(filter);
    }
} 